import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import SEO from "@/components/SEO";

const HERO = "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783935758/5194593295.jpg";

export default function OnTheRoad() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vehicles", { params: { limit: 500 } })
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, []);

  const soldVehicles = useMemo(
    () => vehicles.filter((v) => v.status === "sold" || v.status === "delivered"),
    [vehicles]
  );

  return (
    <div data-testid="on-the-road-page" className="bg-[#0D0D0D] min-h-screen text-white">
      <SEO
        title="Sur nos routes — Elles roulent chez leurs nouveaux propriétaires"
        description="Ces véhicules ont trouvé preneur : Porsche, prestige et sportives livrées par KV International à Rhode-Saint-Genèse. La preuve qu'on trouve preneur vite, au juste prix."
      />

      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="KV International — Sur nos routes" fetchpriority="high" decoding="async" className="w-full h-full object-cover kenburns img-fade" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0D0D0D]" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-16">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A75A] mb-6">Elles roulent déjà</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-light leading-[1.05] max-w-4xl">
            Sur nos routes.
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mt-6 leading-relaxed">
            Ces belles autos ont trouvé preneur. Elles roulent aujourd'hui chez leurs nouveaux propriétaires — la preuve qu'on trouve preneur vite, au juste prix.
          </p>
          <p className="text-white/50 text-sm mt-4">
            {soldVehicles.length} véhicule{soldVehicles.length > 1 ? "s" : ""} livré{soldVehicles.length > 1 ? "s" : ""}
          </p>
        </div>
      </section>

      {/* SECTION — livrées uniquement */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <section data-testid="otr-section-sold" className="scroll-mt-28">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-10 pb-6 border-b border-white/10">
            <div>
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest border rounded-sm px-3 py-1.5 mb-4 text-white/60 border-white/20 bg-white/5">
                Trop tard
              </span>
              <h2 className="font-display tracking-tight font-light leading-[1.05] text-4xl md:text-5xl text-white/90">
                Trop tard, déjà sur nos routes
              </h2>
              <p className="text-white/60 text-base max-w-3xl mt-4 leading-relaxed">
                Envie du même type de modèle ? Laissez-nous vos critères, on vous prévient au prochain arrivage.
              </p>
            </div>
            <span className="text-white/40 text-sm">{soldVehicles.length} véhicule{soldVehicles.length > 1 ? "s" : ""}</span>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => <div key={i} className="bg-[#1E1E1E] border border-white/5 rounded-md aspect-[4/3] animate-pulse" />)}
            </div>
          ) : soldVehicles.length === 0 ? (
            <div className="bg-[#1E1E1E] border border-white/5 rounded-md p-16 text-center">
              <p className="text-white/40 text-sm">Aucun véhicule livré pour le moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
              {soldVehicles.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
            </div>
          )}
        </section>

        {/* Footer CTA */}
        <div className="mt-24 bg-[#1E1E1E] border border-[#C8A75A]/20 rounded-md p-10 lg:p-16 text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Vous cherchez un modèle en particulier ?</p>
          <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-6">On vous prévient au prochain arrivage.</h3>
          <p className="text-white/60 text-base max-w-xl mx-auto mb-8">
            Confiez-nous vos critères — on vous contacte dès qu'une auto correspond à ce que vous cherchez.
          </p>
          <Link
            to="/contact"
            data-testid="otr-cta-contact"
            className="inline-flex items-center gap-3 bg-[#C8A75A] text-black hover:bg-white transition-colors font-semibold px-8 py-4 rounded-md text-sm uppercase tracking-widest"
          >
            Nous contacter <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
